'use client';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  type DialogProps,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';
import { VisuallyHidden } from 'react-aria';
import { Icons } from '../icons';
import { DialogDescription } from './detail-dialog';

type CommandProps = React.ComponentProps<typeof CommandPrimitive>;

function Command({ className, ref, ...props }: CommandProps) {
  return (
    <CommandPrimitive
      ref={ref}
      className={cn(
        'flex size-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        className
      )}
      {...props}
    />
  );
}

type CommandDialogProps = DialogProps & {
  className?: string;
};
const CommandDialog = ({
  children,
  className,
  ...dialogProps
}: CommandDialogProps) => {
  return (
    <Dialog {...dialogProps}>
      <DialogContent className={cn('overflow-hidden p-0 shadow-lg', className)}>
        <VisuallyHidden>
          <DialogTitle>Global sök</DialogTitle>
          <DialogDescription>Sök globalt i systemet</DialogDescription>
        </VisuallyHidden>
        <Command className='[&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:size-5 [&_[cmdk-item]_svg]:size-5 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group]]:px-2 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3'>
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
interface CommandInputProps extends React.ComponentProps<
  typeof CommandPrimitive.Input
> {
  isLoading?: boolean;
  expand?: React.ReactNode;
}

function CommandInput({
  className,
  isLoading,
  expand,
  ref,
  ...props
}: CommandInputProps) {
  return (
    <div className='flex items-center border-b px-3' cmdk-input-wrapper=''>
      {isLoading ? (
        <Icons.loader className='mr-2 size-4 shrink-0 animate-spin opacity-50' />
      ) : (
        <Icons.search className='mr-2 size-4 shrink-0 opacity-50' />
      )}
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
      {expand && expand}
    </div>
  );
}

interface CommandListProps extends React.ComponentProps<
  typeof CommandPrimitive.List
> {
  isLoading?: boolean;
}

function CommandList({
  className,
  isLoading,
  children,
  ref,
  ...props
}: CommandListProps) {
  return (
    <CommandPrimitive.List
      ref={ref}
      className={cn(
        'max-h-[300px] overflow-x-hidden overflow-y-auto',
        className
      )}
      {...props}
    >
      {isLoading && (
        <CommandPrimitive.Loading>Laddar…</CommandPrimitive.Loading>
      )}
      {children}
    </CommandPrimitive.List>
  );
}

type CommandEmptyProps = React.ComponentProps<typeof CommandPrimitive.Empty>;

function CommandEmpty({ ref, ...props }: CommandEmptyProps) {
  return (
    <CommandPrimitive.Empty
      {...props}
      ref={ref}
      className={cn('py-6 text-center text-sm', props.className)}
    />
  );
}

interface CommandGroupProps extends React.ComponentProps<
  typeof CommandPrimitive.Group
> {
  isLoading?: boolean;
}

function CommandGroup({
  className,
  isLoading,
  children,
  ref,
  ...props
}: CommandGroupProps) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cn(
        'overflow-hidden p-1 text-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground',
        className
      )}
      {...props}
    >
      {isLoading && (
        <CommandPrimitive.Loading>
          <div className='my-4 flex size-full items-center justify-center gap-2'>
            <Icons.loader className='animate-spin' /> <p>Laddar…</p>
          </div>
        </CommandPrimitive.Loading>
      )}
      {children}
    </CommandPrimitive.Group>
  );
}

type CommandSeparatorProps = React.ComponentProps<
  typeof CommandPrimitive.Separator
>;

function CommandSeparator({ className, ref, ...props }: CommandSeparatorProps) {
  return (
    <CommandPrimitive.Separator
      ref={ref}
      className={cn('-mx-1 h-px bg-border', className)}
      {...props}
    />
  );
}

type CommandItemProps = React.ComponentProps<typeof CommandPrimitive.Item>;

function CommandItem({ className, ref, ...props }: CommandItemProps) {
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none aria-selected:bg-accent aria-selected:text-foreground',
        className
      )}
      {...props}
    />
  );
}
const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'ml-auto text-xs tracking-widest text-muted-foreground',
        className
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';
export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
